/**
 * Ширина панели ИИ перетаскиванием левого края (agroai-6al.39, журнал №373).
 *
 * Было два состояния — компактная (480 px) и полноэкранная. Теперь между ними
 * ширину задаёт участник: тянет ручку на левом краю мышью или стрелками с
 * клавиатуры (ручка фокусируемая, `role="separator"`). Границы — от
 * {@link MIN_WIDTH} до {@link MAX_SHARE} ширины окна; двойной щелчок по ручке
 * (или Enter) возвращает ширину по умолчанию. Ширина запоминается на
 * пользователя в `localStorage` (с try/catch: в приватном окне хранилища нет —
 * панель просто открывается шириной по умолчанию). Полноэкранный режим
 * остаётся; на узком экране (≤ 640 px) панель во всю ширину и ручки нет.
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { defineMessages } from '../i18n/define';
import { useT } from '../i18n/hooks';

const m = defineMessages({
  aria: { id: 'ai.resize.aria', defaultMessage: 'Ширина панели ИИ-аналитика', description: 'Подпись для диктора ручки на левом краю панели ИИ, которой меняют ширину панели' },
  valueText: { id: 'ai.resize.valueText', defaultMessage: '{width} пикселей', description: 'Текущее значение ручки ширины для диктора; {width} — число пикселей' },
  hint: { id: 'ai.resize.hint', defaultMessage: 'Потяните, чтобы изменить ширину; двойной щелчок — ширина по умолчанию', description: 'Подсказка при наведении на ручку ширины панели ИИ' },
});

export const DEFAULT_WIDTH = 480;
export const MIN_WIDTH = 360;
/** Наибольшая доля окна под панелью. */
export const MAX_SHARE = 0.85;
/**
 * Доля окна, на которую панель открывается заново (05.10): если в прошлый раз
 * её растянули почти на весь экран, повторное открытие — не во весь экран.
 */
export const REOPEN_SHARE = 0.5;
/** Шаг стрелки; с Shift — крупный шаг. */
export const STEP = 16;
export const BIG_STEP = 64;

const KEY = 'agroai-agent-width-v1:';

function viewportWidth(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/** Верхняя граница ширины в этом окне (не меньше нижней). */
export function maxWidth(viewport: number = viewportWidth()): number {
  return Math.max(MIN_WIDTH, Math.floor(viewport * MAX_SHARE));
}

export function clampWidth(value: number, viewport: number = viewportWidth()): number {
  if (!Number.isFinite(value)) return DEFAULT_WIDTH;
  return Math.min(maxWidth(viewport), Math.max(MIN_WIDTH, Math.round(value)));
}

function read(user: string): number | null {
  try {
    const raw = window.localStorage.getItem(KEY + user);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function write(user: string, value: number | null): void {
  try {
    if (value === null) window.localStorage.removeItem(KEY + user);
    else window.localStorage.setItem(KEY + user, String(value));
  } catch {
    /* без хранилища ширина живёт до перезагрузки */
  }
}

/**
 * Ширина панели участника: сохранённая или по умолчанию, ужатая под окно.
 * ``width`` — уже с учётом текущего окна; сохраняется то, что выбрал человек.
 */
export function usePanelWidth(user: string) {
  const [chosen, setChosen] = useState<number | null>(() => read(user));
  const [viewport, setViewport] = useState(viewportWidth);

  useEffect(() => {
    setChosen(read(user));
  }, [user]);

  useEffect(() => {
    const onResize = () => setViewport(viewportWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setWidth = useCallback(
    (value: number) => {
      const next = clampWidth(value);
      setChosen(next);
      write(user, next);
    },
    [user],
  );
  /** Перед повторным открытием: слишком широкая панель ужимается до половины окна. */
  const settle = useCallback(() => {
    const cap = Math.max(DEFAULT_WIDTH, Math.floor(viewportWidth() * REOPEN_SHARE));
    setChosen((current) => {
      if (current === null || current <= cap) return current;
      write(user, cap);
      return cap;
    });
  }, [user]);
  const reset = useCallback(() => {
    setChosen(null);
    write(user, null);
  }, [user]);

  return { width: clampWidth(chosen ?? DEFAULT_WIDTH, viewport), max: maxWidth(viewport), setWidth, reset, settle };
}

interface HandleProps {
  width: number;
  max: number;
  onChange: (width: number) => void;
  onReset: () => void;
  /** Идёт перетаскивание — панель снимает анимацию и выделение текста. */
  onDragging?: (dragging: boolean) => void;
}

/** Ручка на левом краю панели: мышь, касание и клавиатура. */
export function PanelResizeHandle({ width, max, onChange, onReset, onDragging }: HandleProps) {
  const t = useT();
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    drag.current = { startX: event.clientX, startWidth: width };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* указатель уже отпущен — тянем без захвата */
    }
    onDragging?.(true);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    if (!state) return;
    // Ручка слева: движение влево (меньше clientX) расширяет панель.
    onChange(state.startWidth + (state.startX - event.clientX));
  };
  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* захвата не было */
    }
    onDragging?.(false);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? BIG_STEP : STEP;
    const next =
      event.key === 'ArrowLeft'
        ? width + step
        : event.key === 'ArrowRight'
          ? width - step
          : event.key === 'Home'
            ? MIN_WIDTH
            : event.key === 'End'
              ? max
              : null;
    if (event.key === 'Enter') {
      event.preventDefault();
      onReset();
      return;
    }
    if (next === null) return;
    event.preventDefault();
    onChange(next);
  };

  return (
    <div
      className="agent-resize"
      role="separator"
      aria-orientation="vertical"
      aria-label={t(m.aria)}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={max}
      aria-valuenow={width}
      aria-valuetext={t(m.valueText, { width })}
      title={t(m.hint)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
    />
  );
}
